import {useState, useEffect} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {useForecastProjects} from '../hooks/useForecastProjects';
import {useForecastTaskSearch, type ForecastTaskSearchResult} from '../hooks/useForecastTaskSearch';
import {useCreateForecastTask} from '../hooks/useCreateForecastTask';
import {useLinkForecastTaskToTicket} from '../hooks/useLinkForecastTaskToTicket';

interface AddForecastTaskModalProps {
	ticketId: string;
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	/**
	 * ACC-527: fired after a *newly created* task is created and linked, with its
	 * companyTaskId (the numeric T-number). The detail route uses this to open the
	 * native Forecast task modal so the user can fill in assignees/estimate/dates.
	 * Not fired on the link-existing path.
	 */
	onTaskCreated?: (companyTaskId: number) => void;
}

type Mode = 'create' | 'link';

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 28rem;
	border: 1px solid ${p => p.theme.ticketing.color.dark100};
	border-radius: 12px;
	background: #ffffff;
	padding: 24px;
	box-shadow: ${p => p.theme.ticketing.shadow.shadow3};
`;

const Title = styled.h2`
	margin: 0 0 16px;
	font-size: 18px;
	line-height: 28px;
	font-weight: 500;
	color: #0f172a;
`;

const ModeToggle = styled.div`
	margin-bottom: 16px;
	display: flex;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	padding: 4px;
`;

const ModeButton = styled.button<{$active: boolean}>`
	flex: 1 1 0%;
	border: 0;
	border-radius: 6px;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.15s;
	background: ${p => (p.$active ? p.theme.ticketing.color.interactivePrimary : 'transparent')};
	color: ${p => (p.$active ? '#ffffff' : p.theme.ticketing.color.dark500)};
	&:hover {
		background: ${p => (p.$active ? p.theme.ticketing.color.interactivePrimary : p.theme.ticketing.color.bgSubtle)};
	}
	&:focus-visible {
		outline: 2px solid ${p => p.theme.ticketing.color.interactivePrimary};
		outline-offset: 2px;
	}
`;

const Field = styled.div`
	margin-bottom: 12px;
`;

const Label = styled.label`
	display: block;
	margin-bottom: 4px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #475569;
`;

const TextInput = styled.input`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	&:focus {
		outline: 2px solid ${p => p.theme.ticketing.color.interactivePrimary};
		outline-offset: 2px;
		border-color: ${p => p.theme.ticketing.color.interactivePrimary};
		box-shadow: none;
	}
`;

const Select = styled.select`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	background: #ffffff;
	&:focus {
		outline: 2px solid ${p => p.theme.ticketing.color.interactivePrimary};
		outline-offset: 2px;
		border-color: ${p => p.theme.ticketing.color.interactivePrimary};
		box-shadow: none;
	}
`;

const ResultsHint = styled.p`
	margin: 0 0 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #64748b;
`;

const ResultsList = styled.div`
	margin-bottom: 16px;
	max-height: 14rem;
	overflow-y: auto;
	border: 1px solid #e2e8f0;
	border-radius: 4px;
`;

const ResultRow = styled.button<{$selected: boolean}>`
	display: block;
	width: 100%;
	border: 0;
	border-bottom: 1px solid #e2e8f0;
	background: ${p => (p.$selected ? '#eef2ff' : '#ffffff')};
	padding: 8px 12px;
	text-align: left;
	cursor: pointer;
	&:last-child {
		border-bottom: 0;
	}
	&:hover {
		background: ${p => (p.$selected ? '#eef2ff' : '#f8fafc')};
	}
`;

const ResultName = styled.div`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #0f172a;
`;

const ResultMeta = styled.div`
	font: ${p => p.theme.ticketing.font.body3};
	color: #64748b;
`;

const ErrorText = styled.p`
	margin: 0 0 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #dc2626;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

export function AddForecastTaskModal({ticketId, open, onClose, onSuccess, onTaskCreated}: AddForecastTaskModalProps) {
	const [mode, setMode] = useState<Mode>('create');

	// Create-new state.
	const [name, setName] = useState('');
	const [projectId, setProjectId] = useState<string>('');
	const {projects, loading: projectsLoading} = useForecastProjects();

	// Link-existing state.
	const [query, setQuery] = useState('');
	const {search, results, loading: searching} = useForecastTaskSearch();
	const [selected, setSelected] = useState<ForecastTaskSearchResult | null>(null);
	const [searched, setSearched] = useState(false);

	const {submit: createTask, loading: creating} = useCreateForecastTask();
	const {submit: linkTask, loading: linking} = useLinkForecastTaskToTicket();
	const [error, setError] = useState<string | null>(null);

	// Reset transient state whenever the modal is (re)opened or the mode flips.
	useEffect(() => {
		if (!open) return;
		setError(null);
		setSelected(null);
		setSearched(false);
		setQuery('');
	}, [open, mode]);

	// Debounce the link-mode search ~300ms after typing stops; ignore stale runs.
	useEffect(() => {
		if (mode !== 'link') return;
		if (!query.trim()) {
			setSearched(false);
			return;
		}
		let cancelled = false;
		const handle = setTimeout(() => {
			setSelected(null);
			search(query)
				.then(() => {
					if (!cancelled) setSearched(true);
				})
				.catch(() => {
					if (!cancelled) setSearched(true);
				});
		}, 300);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [query, mode, search]);

	if (!open) return null;

	const busy = creating || linking || searching;

	const finish = () => {
		onSuccess?.();
		onClose();
	};

	const handleCreateSubmit = () => {
		const trimmed = name.trim();
		if (!trimmed) {
			setError('Task name is required');
			return;
		}
		// Defence in depth: the submit button is already disabled without a project,
		// so this only fires if that guard is ever bypassed (programmatic submit).
		if (!projectId) {
			setError('Project is required');
			return;
		}
		setError(null);
		createTask({name: trimmed, projectId})
			.then(task =>
				linkTask({ticketId, forecastTaskId: task.id}).then(() => {
					if (task.companyTaskId != null) onTaskCreated?.(task.companyTaskId);
				})
			)
			.then(finish, err => setError(err instanceof Error ? err.message : String(err)));
	};

	const handleLinkSubmit = () => {
		if (!selected) {
			setError('Select a task to link');
			return;
		}
		setError(null);
		linkTask({ticketId, forecastTaskId: selected.id}).then(finish, err =>
			setError(err instanceof Error ? err.message : String(err))
		);
	};

	return (
		<Overlay role="dialog" aria-modal="true" aria-label="Add task">
			<Backdrop onClick={onClose} />
			<Dialog data-testid="add-forecast-task-dialog">
				<Title>Add task</Title>

				<ModeToggle>
					<ModeButton type="button" $active={mode === 'create'} onClick={() => setMode('create')}>
						Create new task
					</ModeButton>
					<ModeButton type="button" $active={mode === 'link'} onClick={() => setMode('link')}>
						Link existing task
					</ModeButton>
				</ModeToggle>

				{mode === 'create' ? (
					<>
						<Field>
							<Label htmlFor="fc-task-name">Task name</Label>
							<TextInput
								id="fc-task-name"
								value={name}
								onChange={e => setName(e.target.value)}
								placeholder="What needs doing?"
							/>
						</Field>
						<Field>
							<Label htmlFor="fc-task-project">Project</Label>
							<Select
								id="fc-task-project"
								value={projectId}
								onChange={e => setProjectId(e.target.value)}
								disabled={projectsLoading}
							>
								<option value="" disabled>
									Select a project…
								</option>
								{projects.map(p => (
									<option key={p.id} value={p.id}>
										{p.name}
									</option>
								))}
							</Select>
							{/* Without this, no available project reads as an inexplicably
							    dead "Create & link" button. */}
							{!projectsLoading && projects.length === 0 && (
								<ResultsHint>
									No projects available. You need access to at least one project to create a task.
								</ResultsHint>
							)}
						</Field>
					</>
				) : (
					<>
						<Field>
							<Label htmlFor="fc-task-search">Search tasks</Label>
							<TextInput
								id="fc-task-search"
								value={query}
								onChange={e => setQuery(e.target.value)}
								placeholder="Search by name or task number"
							/>
						</Field>
						{searching && <ResultsHint>Searching…</ResultsHint>}
						{!searching && searched && results.length === 0 && <ResultsHint>No tasks found.</ResultsHint>}
						{results.length > 0 && (
							<ResultsList>
								{results.map(task => (
									<ResultRow
										key={task.id}
										type="button"
										$selected={selected?.id === task.id}
										onClick={() => setSelected(task)}
									>
										<ResultName>
											{task.companyTaskId != null ? `T${task.companyTaskId} · ` : ''}
											{task.name ?? 'Untitled task'}
										</ResultName>
										<ResultMeta>{task.projectName ?? 'No project'}</ResultMeta>
									</ResultRow>
								))}
							</ResultsList>
						)}
					</>
				)}

				{error && <ErrorText>{error}</ErrorText>}

				<Actions>
					<Button emphasis={EMPHASIS.LOW} disabled={busy} onPress={onClose}>
						Cancel
					</Button>
					{mode === 'create' ? (
						<Button emphasis={EMPHASIS.HIGH} disabled={busy || !projectId} onPress={handleCreateSubmit}>
							{busy ? 'Saving…' : 'Create & link'}
						</Button>
					) : (
						<Button emphasis={EMPHASIS.HIGH} disabled={busy || !selected} onPress={handleLinkSubmit}>
							{busy ? 'Saving…' : 'Link task'}
						</Button>
					)}
				</Actions>
			</Dialog>
		</Overlay>
	);
}
